import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Trash2, ToggleLeft, ToggleRight, RotateCcw, RefreshCw, Database, Terminal, Copy, Check, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getProject } from '@/api/projects'
import { MOCK_BASE_DOMAIN, MOCK_BASE_SCHEME } from '@/lib/constants'
import { useAuth } from '@/hooks/useAuth'
import { listEndpoints, type Endpoint, type HttpMethod } from '@/api/endpoints'
import { listScenarios, updateScenario, deleteScenario, resetScenarioState, getScenarioState, type Scenario, type SequenceConfig, type StatefulCollectionConfig } from '@/api/scenarios'
import { getCollection, deleteCollection } from '@/api/collections'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { ApiClient } from '@/components/api-client/ApiClient'
import { RequestDocsView, hasRequestDocsContent } from '@/components/endpoint/RequestDocsView'
import { RequestDocsEditor } from '@/components/endpoint/RequestDocsEditor'
import { buildApiClientDefaults } from '@/lib/request-docs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AddScenarioDialog } from '@/components/scenario/ScenarioForm'
import { EditScenarioDialog } from '@/components/scenario/EditScenarioDialog'
import { LogViewer } from '@/components/logs/LogViewer'

// ── Method badge ───────────────────────────────────────────────────────────

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET:     'bg-blue-100 text-blue-700 border-blue-200',
  POST:    'bg-green-100 text-green-700 border-green-200',
  PUT:     'bg-orange-100 text-orange-700 border-orange-200',
  PATCH:   'bg-purple-100 text-purple-700 border-purple-200',
  DELETE:  'bg-red-100 text-red-700 border-red-200',
  HEAD:    'bg-gray-100 text-gray-600 border-gray-200',
}

function MethodBadge({ method }: { method: HttpMethod }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-2 py-0.5 text-xs font-mono font-semibold',
        METHOD_COLORS[method],
      )}
    >
      {method}
    </span>
  )
}

// ── Scenario type badge ────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  static: 'Static',
  sequence: 'Sequence',
  conditional: 'Conditional',
  stateful_collection: 'Stateful',
}

// ── Copy button ───────────────────────────────────────────────────────────

function CopyButton({ value, className }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()

  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      onClick={handleCopy}
      title={t('common.copy')}
      className={cn('inline-flex items-center text-muted-foreground hover:text-foreground transition-colors', className)}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

// ── Toggle enabled button ──────────────────────────────────────────────────

function ToggleEnabledButton({ scenario, endpointId }: { scenario: Scenario; endpointId: string }) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: () => updateScenario(scenario.id, { enabled: !scenario.enabled }),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      toast.success(updated.enabled ? t('endpoint.scenario.enabledToast') : t('endpoint.scenario.disabledToast'))
    },
    onError: () => {
      toast.error(t('endpoint.scenario.updateFailed'))
    },
  })

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-8 w-8', scenario.enabled ? 'text-primary' : 'text-muted-foreground')}
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      title={scenario.enabled ? t('endpoint.scenario.disable') : t('endpoint.scenario.enable')}
    >
      {scenario.enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
    </Button>
  )
}

// ── Sequence counter + inline reset ───────────────────────────────────────

function SequenceCounter({ scenario }: { scenario: Scenario }) {
  const queryClient = useQueryClient()
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)
  const { t } = useTranslation()

  const config = scenario.config as unknown as SequenceConfig
  const stepCount = config.steps?.length ?? 0

  const { data, refetch } = useQuery({
    queryKey: ['scenario-state', scenario.id],
    queryFn: () => getScenarioState(scenario.id),
    staleTime: 0,
    refetchInterval: 2000,
  })

  const resetMutation = useMutation({
    mutationFn: () => resetScenarioState(scenario.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['scenario-state', scenario.id] })
    },
  })

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true)
    await refetch()
    setIsManualRefreshing(false)
  }

  const counts = data?.counts ?? {}
  const keys = Object.keys(counts)

  let label: string
  if (keys.length === 0) {
    label = `0 / ${stepCount}`
  } else if (counts['g'] !== undefined) {
    // global scope: 現在カウント / ステップ数
    label = `${counts['g']} / ${stepCount}`
  } else {
    // per_ip / per_header: number of active scopes
    label = t('endpoint.activeScopes', { count: keys.length })
  }

  const isEmpty = keys.length === 0

  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <span className={cn('tabular-nums', !isEmpty && 'text-foreground font-medium')}>{label}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-4 w-4 text-muted-foreground hover:text-blue-500"
        onClick={() => void handleManualRefresh()}
        disabled={isManualRefreshing}
        title={t('endpoint.sequence.refreshNow')}
      >
        <RefreshCw className={cn('h-3 w-3', isManualRefreshing && 'animate-spin')} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-4 w-4 text-muted-foreground hover:text-orange-500 disabled:opacity-30"
        onClick={() => resetMutation.mutate()}
        disabled={resetMutation.isPending || isEmpty}
        title={t('endpoint.sequence.resetCounter')}
      >
        <RotateCcw className="h-3 w-3" />
      </Button>
    </span>
  )
}

// ── Delete scenario button ─────────────────────────────────────────────────

function DeleteScenarioButton({ scenario, endpointId }: { scenario: Scenario; endpointId: string }) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: () => deleteScenario(scenario.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      setOpen(false)
      toast.success(t('endpoint.scenario.deleteSuccess'))
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('endpoint.scenario.deleteFailed'))
    },
  })

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!mutation.isPending) { setOpen(v); if (!v) setError(null) } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('endpoint.scenario.deleteTitle')}</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{scenario.name}</span> {t('endpoint.scenario.deleteDescription')}{t('common.cannotUndo')}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Collection panel ───────────────────────────────────────────────────────

function CollectionTable({ projectId, stateKey }: { projectId: string; stateKey: string }) {
  const queryClient = useQueryClient()
  const [resetOpen, setResetOpen] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const { t } = useTranslation()

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['collection', projectId, stateKey],
    queryFn: () => getCollection(projectId, stateKey),
    staleTime: 0,
    refetchInterval: 3_000,
  })

  const resetMutation = useMutation({
    mutationFn: () => deleteCollection(projectId, stateKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collection', projectId, stateKey] })
      setResetOpen(false)
    },
    onError: (err) => {
      setResetError(err instanceof ApiError ? err.message : t('endpoint.collection.resetFailed'))
    },
  })

  const items = data?.items ?? []
  // 全アイテムのキーを収集 (最大6列)
  const columns = Array.from(
    new Set(items.flatMap(item => Object.keys(item)))
  ).slice(0, 6)

  function cellStr(v: unknown): string {
    if (v === null || v === undefined) return '—'
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v).slice(0, 40)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-muted-foreground">{stateKey}</span>
        <Badge variant="secondary" className="text-xs">{t('endpoint.collectionCount', { count: data?.count ?? 0 })}</Badge>
        <div className="ml-auto flex gap-1">
          <Button
            variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-500"
            onClick={() => void refetch()} disabled={isLoading || isRefetching}
            title={t('endpoint.sequence.refreshNow')}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          </Button>
          <Dialog open={resetOpen} onOpenChange={(v) => { if (!resetMutation.isPending) { setResetOpen(v); if (!v) setResetError(null) } }}>
            <DialogTrigger asChild>
              <Button
                variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                title={t('endpoint.collection.resetTitle')}
                disabled={items.length === 0}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{t('endpoint.collection.resetTitle')}</DialogTitle>
                <DialogDescription>
                  <span className="font-mono font-medium">{stateKey}</span> {t('endpoint.collection.resetDescription')}
                </DialogDescription>
              </DialogHeader>
              {resetError && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{resetError}</p>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetMutation.isPending}>{t('common.cancel')}</Button>
                <Button variant="destructive" onClick={() => resetMutation.mutate()} disabled={resetMutation.isPending}>
                  {resetMutation.isPending ? t('common.resetting') : t('common.reset')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}

      {!isLoading && items.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">{t('endpoint.collection.noItems')}</p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map(col => (
                  <TableHead key={col} className="text-xs font-mono h-8">{col}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={i}>
                  {columns.map(col => (
                    <TableCell key={col} className="text-xs font-mono py-1 max-w-[160px] truncate">
                      {cellStr(item[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ── Action URL list ────────────────────────────────────────────────────────

const ACTION_ORDER = ['create', 'list', 'get', 'update', 'delete'] as const

function ActionUrlList({ config, slug }: { config: StatefulCollectionConfig; slug: string }) {
  const base = `${slug}.${MOCK_BASE_DOMAIN}`
  const { t } = useTranslation()
  const rows = ACTION_ORDER
    .filter((name) => config.actions[name] != null)
    .map((name) => {
      const action = config.actions[name]!
      return {
        name,
        label: t(`endpoint.actionLabels.${name}`),
        method: action.method as HttpMethod,
        path: action.path_match,
        url: `${MOCK_BASE_SCHEME}://${base}${action.path_match}`,
      }
    })

  if (rows.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mock URL</p>
      <div className="rounded-md border divide-y bg-background">
        {rows.map((row) => (
          <div key={row.name} className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-xs text-muted-foreground w-7 shrink-0">{row.label}</span>
            <MethodBadge method={row.method} />
            <code className="text-xs font-mono text-foreground break-all">{row.url}</code>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Collection panel ───────────────────────────────────────────────────────

function CollectionPanel({
  projectId,
  mockSubdomain,
  scenarios,
}: {
  projectId: string
  mockSubdomain: string
  scenarios: Scenario[]
}) {
  const { t } = useTranslation()
  const stateful = scenarios.filter(s => s.type === 'stateful_collection' && s.enabled)
  if (stateful.length === 0) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">{t('endpoint.collection.title')}</h3>
      </div>
      {stateful.map(scenario => {
        const config = scenario.config as unknown as StatefulCollectionConfig
        return (
          <div key={scenario.id} className="rounded-md border bg-muted/20 p-4 space-y-4">
            <p className="text-sm font-medium">{scenario.name}</p>
            {mockSubdomain && <ActionUrlList config={config} slug={mockSubdomain} />}
            <CollectionTable projectId={projectId} stateKey={config.state_key} />
          </div>
        )
      })}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

export function EndpointPage() {
  const { projectId, endpointId } = useParams<{ projectId: string; endpointId: string }>()
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  })

  const { data: endpoints } = useQuery({
    queryKey: ['endpoints', projectId],
    queryFn: () => listEndpoints(projectId!),
    enabled: !!projectId,
  })

  const { data: scenarios, isLoading, isError } = useQuery({
    queryKey: ['scenarios', endpointId],
    queryFn: () => listScenarios(endpointId!),
    enabled: !!endpointId,
  })

  const endpoint: Endpoint | undefined = endpoints?.find((ep) => ep.id === endpointId)

  if (!projectId || !endpointId) return null

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-5xl p-6 space-y-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">{t('endpoint.projects')}</Link>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <Link to={`/projects/${projectId}`} className="hover:text-foreground transition-colors">
            {project?.name ?? '...'}
          </Link>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span className="text-foreground font-medium font-mono">
            {endpoint ? `${endpoint.method} ${endpoint.path}` : '...'}
          </span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {endpoint && <MethodBadge method={endpoint.method} />}
              <h1 className="text-2xl font-bold tracking-tight font-mono">
                {endpoint?.path ?? t('common.loading')}
              </h1>
            </div>
            {endpoint?.description && (
              <p className="text-sm text-muted-foreground">{endpoint.description}</p>
            )}
            {project && endpoint && (
              <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5 flex-wrap">
                Mock URL:
                <span className="text-foreground">
                  {user?.domain ? `${user.domain}--${project.slug}` : project.slug}.{MOCK_BASE_DOMAIN}{endpoint.path}
                </span>
                <CopyButton value={`${MOCK_BASE_SCHEME}://${user?.domain ? `${user.domain}--${project.slug}` : project.slug}.${MOCK_BASE_DOMAIN}${endpoint.path}`} />
              </p>
            )}
          </div>
          {projectId && endpointId && (
            <AddScenarioDialog projectId={projectId} endpointId={endpointId} />
          )}
        </div>

        {/* Scenario table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>{t('endpoint.scenario.name')}</TableHead>
                <TableHead className="hidden sm:table-cell">{t('endpoint.scenario.type')}</TableHead>
                <TableHead className="w-20 text-center">{t('endpoint.scenario.priority')}</TableHead>
                <TableHead className="w-20 text-right">{t('endpoint.scenario.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-destructive">
                    {t('endpoint.scenario.loadFailed')}
                  </TableCell>
                </TableRow>
              )}
              {scenarios && scenarios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    {t('endpoint.scenario.none')}
                  </TableCell>
                </TableRow>
              )}
              {scenarios?.map((sc) => (
                <TableRow key={sc.id} className={cn(!sc.enabled && 'opacity-50')}>
                  <TableCell>
                    {sc.enabled ? (
                      <span className="inline-block h-2 w-2 rounded-full bg-green-500" title={t('endpoint.scenario.enabled')} />
                    ) : (
                      <span className="inline-block h-2 w-2 rounded-full bg-gray-300" title={t('endpoint.scenario.disabled')} />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{sc.name}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {TYPE_LABELS[sc.type] ?? sc.type}
                      </Badge>
                      {sc.type === 'sequence' && <SequenceCounter scenario={sc} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-sm">{sc.priority}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <ToggleEnabledButton scenario={sc} endpointId={endpointId} />
                      <EditScenarioDialog scenario={sc} projectId={projectId} endpointId={endpointId} />
                      <DeleteScenarioButton scenario={sc} endpointId={endpointId} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mock URL hint */}
        {scenarios && scenarios.length > 0 && project && endpoint && (
          <div className="rounded-md border bg-muted/40 p-4 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('endpoint.testCommand')}</p>
              <CopyButton
                value={`curl -s ${endpoint.method !== 'GET' ? `-X ${endpoint.method} ` : ''}${MOCK_BASE_SCHEME}://${user?.domain ? `${user.domain}--${project.slug}` : project.slug}.${MOCK_BASE_DOMAIN}${endpoint.path}`}
              />
            </div>
            <p className="font-mono text-sm break-all">
              curl -s {endpoint.method !== 'GET' ? `-X ${endpoint.method} ` : ''}{MOCK_BASE_SCHEME}://{user?.domain ? `${user.domain}--${project.slug}` : project.slug}.{MOCK_BASE_DOMAIN}{endpoint.path}
            </p>
          </div>
        )}

        {/* Collection panel */}
        {scenarios && projectId && (
          <CollectionPanel
            projectId={projectId}
            mockSubdomain={project ? (user?.domain ? `${user.domain}--${project.slug}` : project.slug) : ''}
            scenarios={scenarios}
          />
        )}

        {/* Request docs */}
        {project && endpoint && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">{t('endpoint.requestDocs.sectionTitle')}</h3>
              </div>
              <RequestDocsEditor endpoint={endpoint} projectId={projectId} />
            </div>
            {hasRequestDocsContent(endpoint.request_docs) ? (
              <div className="rounded-md border bg-muted/10 p-4">
                <RequestDocsView requestDocs={endpoint.request_docs} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('endpoint.requestDocs.empty')}</p>
            )}
          </div>
        )}

        {/* API Client */}
        {project && endpoint && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">{t('endpoint.apiClient')}</h3>
            </div>
            <div className="rounded-md border bg-muted/10 p-4">
              <ApiClient
                key={JSON.stringify(endpoint.request_docs)}
                defaultMethod={endpoint.method}
                {...buildApiClientDefaults(
                  endpoint.path,
                  endpoint.request_docs,
                  `${MOCK_BASE_SCHEME}://${user?.domain ? `${user.domain}--${project.slug}` : project.slug}.${MOCK_BASE_DOMAIN}`,
                )}
              />
            </div>
          </div>
        )}

        {/* Request Log */}
        {projectId && endpointId && (
          <LogViewer projectId={projectId} endpointId={endpointId} />
        )}

      </div>
    </div>
  )
}
